import * as React from "react";
import { useContext, useEffect, useState, FunctionComponent } from "react";
import {
  SliderItemProps,
  SliderItemStatusType,
  SliderItemOnInteractInnerProps,
} from "./interfaces";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../state";
import { AvailableSource } from "seenspire-api-client";
import { chain, find, isEmpty } from "lodash";
import { buttonType, Block } from "../../../seenspire-library";
import { IChannelData, ActiveChannel, Channel } from "../../interfaces";
import SliderActiveItem from "./SliderActiveItem";
import {
  generateInitialChannelItemData,
  generateRegionsOptions,
  DEFAULT_REGION_ID,
  DEFAULT_LANGUAGE_ID,
  requiresCustomFieldsOnly,
  getDefaultRegionForChannel,
  getDefaultLanguageForChannel,
  safeParseValue,
} from "./SliderItemOnInteractLogic";
import { generateMyChannel } from "./MethodsAndLogic";
import { OTHER_LANGUAGE_CODES } from "../../backend/consts";
import { getToken } from "../../backend/session";
import { sortBy } from "lodash";
import { useNavigate } from "react-router-dom";

type LanguageIso = {
  iso639_1: string;
  iso639_2: string;
  name: string;
  nativeName: string;
};

export const generateDefaultCustomFields = (channel: Channel) => {
  return channel.slug === "datacall_stocks" ? [""] : {};
};
const SliderItemOnInteractConfig: FunctionComponent<SliderItemProps> = (
  props
) => {
  const navigate = useNavigate();
  const history = {
    push: (path: string, state?: any) => navigate(path, { state }),
    replace: (path: string, state?: any) => navigate(path, { state, replace: true }),
  };
  const {
    channel,
    isActive,
    openItem,
    myChannels,
    setMyChannels,
    itemType,
    onOpenItem,
    setChannelInPreview,
  } = props;
  const [status, setStatus] = useState<SliderItemStatusType>("INIT");
  const { t } = useTranslation();
  const {
    client,
    slug,
    activated,
    setConfirmEmailModalIsOpen,
    setNotificationConfig,
    hasRole,
  } = useContext(AppContext);
  const [data, setData] = useState<IChannelData>();
  const [customFields, setCustomFields] = useState<Record<string, any>>(generateDefaultCustomFields(channel));
  const [selectedSource, setSelectedSource] = useState<any>();
  const [transportList, setApiTransportList] = useState<any[]>([]);
  const [apiCityLanguageList, setApiCityLanguageList] = useState<any[]>([]);
  const [regionLanguageList, setRegionLanguageList] = useState<any[]>([]);
  const [localWeatherLanguageList, setLocalWeatherLanguageList] = useState<
    any[]
  >([]);

  const [selectedRegionLanguage, setSelectedRegionLanguage] = useState<any>({
    label: "United States",
    value: "United States",
    slug: "US",
  });
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>();
  const [selectedPlace, setSelectedPlace] = useState<string>();

  const [selectedLanguage, setSelectedLanguage] = useState<any>();
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [initialOpenItem, setInitialOpenItem] = useState(openItem);
  const collectData = () => {
    if (channel.precurated_template) {
      return {};
    }
    return (
      (openItem && openItem.data) ||
      generateInitialChannelItemData(channel, myChannels)
    );
  };

  useEffect(() => {
    if (itemType === "open" && !openItem) return;
    channel.precurated_template && setCanSubmit(true);
    setData(collectData());
    // @see issue
    // isActive and openItem removed from the dependency array
    // when user selects language and region he could go outside
    // the box and when he moves the mouse back to the box border, the data is reset.
    // TODO: maybe there's some way to fix it differently?
  }, [myChannels, channel]);

  useEffect(() => {
    if (!data || channel.precurated_template) return;
    const source = channel.available_sources.find(
      (d) => d.source.id === data.source
    );
    source && setSelectedSource(source);
  }, [data]);

  useEffect(() => {
    if (!data || channel.precurated_template) return;
    const _data = { ...data, custom_fields: customFields };
    setData(_data);
    setCanSubmit(true);
  }, [customFields]);

  useEffect(() => {
    if (
      typeof channel.slug === "string" &&
      !channel.slug.includes("local_weather") &&
      !channel.slug.includes("weather_cities") &&
      !channel.slug.includes("traffic") &&
      !channel.slug.includes("airquality") &&
      !channel.slug.includes("here_board")
    )
      return;

    if (!apiCityLanguageList || Array.isArray(customFields)) return;

    const langs = apiCityLanguageList
      .filter((_l: LanguageIso) => {
        if (_l.iso639_1) {
          return OTHER_LANGUAGE_CODES.includes(_l.iso639_1.toLocaleUpperCase());
        }
        return false;
      })
      .map((_l: LanguageIso) => ({
        label: _l.name,
        slug: _l.iso639_1.toLocaleUpperCase(),
        value: _l.iso639_1,
      }));

    const EN = {
      label: "English",
      slug: "EN",
      value: "en",
    };

    setSelectedLanguage(langs[0] || EN);
    const weatherLangs = [EN, ...langs];

    // Initialize with default English language if no languages are available
    const defaultLanguages = [{
      iso639_1: "en",
      iso639_2: "eng",
      name: "English",
      nativeName: "English"
    }];

    setCustomFields({
      ...customFields,
      weatherLangs,
      apilang: (langs[0] || EN).value,
      languages: apiCityLanguageList.length ? apiCityLanguageList : defaultLanguages
    });
    setLocalWeatherLanguageList(weatherLangs);
  }, [apiCityLanguageList, channel]);

  // Effect for when selected language
  // changes and we need to update the
  // customFields to include it.
  useEffect(() => {
    if (!selectedLanguage || Array.isArray(customFields)) return;
    if (data && data?.custom_fields?.apilang !== selectedLanguage.value) {
      setCustomFields({ ...customFields, apilang: selectedLanguage.value });

      setData({
        ...data,
        custom_fields: {
          ...data?.custom_fields,
          apilang: selectedLanguage.value,
        },
      });
    }
  }, [selectedLanguage, channel, data, customFields]);

  useEffect(() => {
    if (!selectedSource || !data || channel.precurated_template) return;

    const regionLanguageList = generateRegionsOptions(
      channel,
      selectedSource,
      myChannels
    );
    regionLanguageList && setRegionLanguageList(regionLanguageList);

    const filteredRegionLanguageList = chain(regionLanguageList)
      // .filter((item) => !item.isInMyChannels)
      .value();

    let _selectedRegionLanguage;
    if (initialOpenItem && initialOpenItem.data) {
      _selectedRegionLanguage =
        find(
          filteredRegionLanguageList,
          (item: any) => item.slug === "GLOBAL"
        ) ||
        find(
          filteredRegionLanguageList,
          (item: any) =>
            item.value ===
            `${initialOpenItem.data.region}_${initialOpenItem.data.language}`
        );
      setInitialOpenItem(null);
    } else {
      // Adding a new feed: default to a variant that is NOT already added, so
      // we never preselect (and then let the user re-add) an existing one.
      // See SEE-452.
      const isAddable = (item: any) => !item.hidden && !item.isInMyChannels;
      _selectedRegionLanguage =
        find(
          filteredRegionLanguageList,
          (item: any) => item.slug === "GLOBAL" && isAddable(item)
        ) ||
        find(
          filteredRegionLanguageList,
          (item: any) =>
            item.value === `${DEFAULT_REGION_ID}_${DEFAULT_LANGUAGE_ID}` &&
            isAddable(item)
        ) ||
        find(filteredRegionLanguageList, isAddable);
    }

    // Every selectable variant is already added: use a non-submittable
    // placeholder (isInMyChannels:true) so the canSubmit effect disables the
    // ADD FEED button and the same feed can't be added twice. See SEE-452.
    _selectedRegionLanguage = _selectedRegionLanguage || {
      label: "All options have been added",
      value: `${getDefaultRegionForChannel(channel)}_${getDefaultLanguageForChannel(channel)}`,
      isInMyChannels: true,
    };
    setSelectedRegionLanguage(_selectedRegionLanguage);

    // Safely parse the region and language, handling the case where value might be malformed
    const [regionStr, languageStr] = _selectedRegionLanguage.value.split("_");
    setData({
      ...data,
      region: safeParseValue(regionStr, getDefaultRegionForChannel(channel)),
      language: safeParseValue(languageStr, getDefaultLanguageForChannel(channel)),
    });
  }, [selectedSource]);

  useEffect(() => {
    if (!selectedSource || !data || channel.precurated_template) return;

    const regionLanguageList = generateRegionsOptions(
      channel,
      selectedSource,
      myChannels
    );

    const reorderedList = sortBy(regionLanguageList, ({ slug }) =>
      slug === "GLOBAL" ? 0 : 1
    );
    regionLanguageList && setRegionLanguageList(reorderedList);
  }, [selectedSource]);

  useEffect(() => {
    if (requiresCustomFieldsOnly(channel)) return;
    // The selected variant is already in the user's channels (or every variant
    // is, via the "All options have been added" placeholder): block re-adding
    // the same feed. See SEE-452.
    if (selectedRegionLanguage && (selectedRegionLanguage as any).isInMyChannels) {
      setCanSubmit(false);
      setStatus(
        channel && channel.slug === "public_transport" ? "DISABLED" : "INIT"
      );
      return;
    }
    if (
      selectedRegionLanguage &&
      selectedRegionLanguage.value !== "null_null"
    ) {
      setCanSubmit(true);
      if (channel && channel.slug === "public_transport") {
        setStatus("DISABLED");
      } else {
        setStatus("INIT");
      }
    } else if (channel && channel.slug === "public_transport") {
      setCanSubmit(false);
      setStatus("DISABLED");
    } else {
      setCanSubmit(true);
      setStatus("INIT");
    }
  }, [selectedRegionLanguage, channel]);

  const onSourceChange = (value: any) => {
    if (!data) return;
    const source = channel.available_sources.find(
      (d) => d.source.id === parseInt(value.value)
    );
    source && setSelectedSource(source);
    setData({ ...data, source: parseInt(value.value) });
  };

  const onRegionLanguageChange = (value: any) => {
    if (!data) return;
    const region = parseInt(value.value.split("_")[0]);
    const language = parseInt(value.value.split("_")[1]);
    setSelectedRegionLanguage(value);
    setData({ ...data, region, language });
  };

  const onNationalRegionLanguageChange = (value: any) => {
    if (!data) return;
    setSelectedRegionLanguage(value);
    setData({
      ...data,
      region: safeParseValue(value.regionId, getDefaultRegionForChannel(channel)),
      language: 1
    });
  };

  const onTimezoneChange = (value: any) => {
    if (!data) return;
    setSelectedTimeZone(value.value);
    setData({
      ...data,
      custom_fields: { ...data.custom_fields, timezone: value.value },
    });
  };

  const onPlaceChange = (value: any) => {
    if (!data) return;
    setSelectedPlace(value.value);
    if (value.value === null) {
      const { place, place_name, ...rest } = data.custom_fields;
      setData({
        ...data,
        custom_fields: {
          ...rest,
        },
      });
    } else {
      setData({
        ...data,
        custom_fields: {
          ...data.custom_fields,
          place: value.value,
          place_name: value.label,
        },
      });
    }
  };

  const onLanguageChange = (language: any) => {
    if (!data) return;

    // For national weather.
    let region = selectedRegionLanguage.regionId;

    // For all the rest ???
    if (!region) {
      region = selectedRegionLanguage.value.split("_")[0];
    }

    setData({
      ...data,
      region: safeParseValue(region, getDefaultRegionForChannel(channel)),
      language: 1
    });
    setSelectedLanguage(language);
  };

  const renderStatusColor = (): buttonType => {
    switch (status) {
      case "DISABLED":
        return "cta";
      case "INIT":
        return "cta";
      case "DONE":
        return "success";
      case "IN_PROGRESS":
        return "cta";
      case "FAILED":
        return "alert";
      default:
        return "cta";
    }
  };
  const emitNotification = (newChannel: any) => {
    // @TODO use enum for channel types
    if (
      newChannel.slug &&
      newChannel.slug.includes("datacall_stocks") &&
      find(newChannel.custom_fields, { isAvailableWhenAdded: false })
    ) {
      const stockNames = chain(newChannel.custom_fields)
        .filter((stock) => !stock.isAvailableWhenAdded)
        .map((stock) => stock.name)
        .value();
      setNotificationConfig({
        modalSize: "large",
        skipDontShow: true,
        icon: "error",
        subTitle: "Stock quotes requested",
        title: " ",
        confirmLabel: "Got it",
        description: (
          <Block>
            <Block>{t("feeds.stock_request_title")}</Block>
            <Block
              display="grid"
              textAlign="center"
              padding={{ all: 10 }}
              gridGap={10}
              justifyContent="center"
            >
              {stockNames.map((name) => (
                <Block key={name} textAlign="center">
                  {name}
                </Block>
              ))}
            </Block>
            <Block>{t("feeds.stock_request_description")}</Block>
          </Block>
        ),
      });
    }
  };

  const submit = async () => {
    if (hasRole("ROLE_CURATOR")) {
      setStatus("FAILED");
      return;
    }
    if (!data) return;

    let region = selectedRegionLanguage.regionId;
    if (!region) {
      region = selectedRegionLanguage.value.split("_")[0];
    }

    let cloneData = Object.assign({}, data);

    // Ensure language is set to 1 for national weather feeds
    if (channel.slug === "weather_cities") {
      cloneData.language = 1;
    }

    // Use defensive parsing instead of hardcoded values
    cloneData.region = safeParseValue(cloneData.region, getDefaultRegionForChannel(channel));
    cloneData.language = safeParseValue(cloneData.language, getDefaultLanguageForChannel(channel));

    if (!activated) return setConfirmEmailModalIsOpen(true);

    if (
      !data ||
      (!canSubmit &&
        channel.slug !== "weather_cities" &&
        channel.slug !== "here_board") ||
      status === "IN_PROGRESS" ||
      status === "DONE"
    )
      return;

    setStatus("IN_PROGRESS");

    try {
      let response;

      // Check if this is a feed type (showAddContentOnly) - use customs endpoint
      if (props.showAddContentOnly) {
        const feedType = (channel as any).type?.slug;
        const currentToken = getToken();
        const fetchResponse = await fetch(`${process.env.REACT_APP_API}/api/activechannels/customs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken.access_token}`,
          },
          body: JSON.stringify({
            type: feedType
          })
        });

        if (fetchResponse.ok) {
          response = await fetchResponse.json();
          // Redirect to settings page immediately
          history.push(`/${slug}/feed/${response.code}/settings`);
          return;
        } else {
          throw new Error(`API call failed with status: ${fetchResponse.status}`);
        }
      } else {
        // Regular feed creation
        const { id } = channel.precurated_template
          ? await client.ActiveChannels.postCustomChannel({
            template_id: channel.id,
            type: "SOCIAL",
          })
          : await client.ActiveChannels.postInfoChannel(cloneData);

        response = { id };
      }

      // Only continue with regular feed processing if not a feed type (feed types redirect immediately)
      if (!props.showAddContentOnly && response && response.id) {
        const newChannel = (await client.ActiveChannels.getActiveChannelById(
          String(response.id)
        )) as any;
        const _myChannels = [generateMyChannel(newChannel), ...myChannels];
        setMyChannels(_myChannels);

        emitNotification(newChannel);

        if ((window as any).pendo) {
          console.log("[Track: FEED_ADD]");
          (window as any).pendo.track("FEED_ADD", newChannel);
        }

        setStatus("DONE");
        // force close the item
        setTimeout(() => {
          console.log("INIT");
          onOpenItem(false, false, true);
          setData(collectData());
          setCustomFields(generateDefaultCustomFields(channel));
          setApiCityLanguageList([]);
          setApiTransportList([]);
          setSelectedPlace(undefined);
          setStatus("INIT");
        }, 1000);
      }
    } catch (error) {
      setStatus("FAILED");
      console.error(error);
    }
  };

  const innerProps: SliderItemOnInteractInnerProps = {
    data,
    onSourceChange,
    selectedRegionLanguage,
    selectedLanguage,
    regionLanguageList,
    onRegionLanguageChange,
    onNationalRegionLanguageChange,
    selectedTimeZone,
    onTimezoneChange,
    onLanguageChange,
    selectedPlace,
    onPlaceChange,
    localWeatherLanguageList,
    canSubmit,
    status,
    renderStatusColor,
    setCustomFields,
    customFields,
    submit,
    transportList,
    setApiTransportList,
    setChannelInPreview,
    setApiCityLanguageList,
    setCanSubmit,
  };
  // if (channel.slug === "news_top") {
  //  console.log(innerProps.regionLanguageList);
  // }
  return (
    <>
      {itemType === "active" && <SliderActiveItem {...props} {...innerProps} />}
      {/* {itemType == 'open' && <SliderOpenItem {...innerProps} {...props} />} */}
    </>
  );
};

export default SliderItemOnInteractConfig;
